import { useCallback, useMemo } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { useUrlSyncedFilters } from '@/hooks/useUrlSyncedFilters';
import { getMonthToDateRange } from '@/utils/date';
import { DATE_RANGE_FILTER_KEYS } from '@/utils/filterFields';
import { REGION_REPORT_TAB, type RegionReportTab } from '../constants/marketPromotion.constants';
import type { MpRegionFilterSearch } from '../types/marketPromotion.types';
import { toMpRegionFilterSearch, toMpRegionFilterValues } from '../utils/mpFilters';

const route = getRouteApi('/_auth/market-promotion/region-summary/');

export type MpRegionParams = MpRegionFilterSearch & { dateFrom: string; dateTo: string };

export function useMpRegionFilters() {
  const search = route.useSearch();
  const navigate = route.useNavigate();

  const filterSearch = useMemo((): MpRegionFilterSearch => {
    const { tab: _openTab, q: _searchText, ...rest } = search;
    return rest;
  }, [search]);

  const defaultPeriod = useMemo(() => getMonthToDateRange(), []);
  const appliedValues = useMemo(
    () => toMpRegionFilterValues(filterSearch, defaultPeriod),
    [filterSearch, defaultPeriod],
  );

  const normalizeFilterValues = useCallback(
    (values: Record<string, string>) =>
      toMpRegionFilterValues(toMpRegionFilterSearch(values), defaultPeriod),
    [defaultPeriod],
  );

  const filters = useUrlSyncedFilters({
    appliedValues,
    shouldWaitForApply: true,
    defaultValues: defaultPeriod,
    rangeKeys: DATE_RANGE_FILTER_KEYS,
    normalizeValues: normalizeFilterValues,
    onApply: (values) => {
      void navigate({
        search: (previous) => ({ ...previous, ...toMpRegionFilterSearch(values) }),
        replace: true,
      });
    },
  });

  const params = useMemo(
    (): MpRegionParams => ({
      ...filterSearch,
      dateFrom: filterSearch.dateFrom ?? defaultPeriod.dateFrom,
      dateTo: filterSearch.dateTo ?? defaultPeriod.dateTo,
    }),
    [filterSearch, defaultPeriod],
  );

  const activeTab: RegionReportTab = search.tab ?? REGION_REPORT_TAB.regions;

  const setActiveTab = useCallback(
    (nextTab: RegionReportTab) => {
      void navigate({ search: (previous) => ({ ...previous, tab: nextTab }), replace: true });
    },
    [navigate],
  );

  const applySearchText = useCallback(
    (text: string | undefined) => {
      void navigate({ search: (previous) => ({ ...previous, q: text }), replace: true });
    },
    [navigate],
  );

  return {
    ...filters,
    params,
    activeTab,
    setActiveTab,
    appliedSearchText: search.q,
    applySearchText,
  };
}
